/** @jsx h */
import { h, render } from 'preact';
import YourLists from '../components/YourLists';
import { getOrderLists } from '../objects/your-lists/orderListApi';
import { clearAllEdits } from '../objects/your-lists/listEdits';

// Start reading the lists from the server as soon as this script runs, rather
// than after DOMContentLoaded — the page waits on it before rendering them
// (see YourLists). The bundle loads on every page template (page-map-js), but
// it's deferred, so the mount point is already parsed if this is Your Lists.
// Demo mode previews DEMO_LISTS, so it skips the request.
const freshLists =
  document.getElementById('your-lists') &&
  !window.location.search.includes('yl-demo')
    ? getOrderLists()
    : null;

/** Safely parse a JSON <script> child of the mount point. */
function readJson(mountPoint, selector, fallback) {
  const el = mountPoint.querySelector(selector);
  if (!el) return fallback;
  try {
    const parsed = JSON.parse(el.textContent);
    return parsed == null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
}

/**
 * Decode HTML entities (e.g. `&#39;` -> `'`) that Liquid's `t` filter adds
 * when escaping translation strings for HTML output.
 */
function decodeEntities(value) {
  if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

/** Decode every string value in the i18n map. */
function decodeI18n(i18n) {
  return Object.keys(i18n).reduce((acc, key) => {
    acc[key] = decodeEntities(i18n[key]);
    return acc;
  }, {});
}

/**
 * Normalize the initial lists payload. Lists are now stored one per company
 * metafield (custom.order_list_N), so the liquid emits `{ key, list }` entries.
 * Flatten each into its list object, attaching `metafieldKey` (the slot the
 * list lives in) and a stable `id` (falling back to the slot key). Plain list
 * objects (e.g. demo data) pass through unchanged.
 */
function normalizeLists(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!entry) return null;
      if (entry.list && typeof entry.list === 'object') {
        return {
          ...entry.list,
          metafieldKey: entry.key,
          id: entry.list.id || entry.key,
        };
      }
      return entry;
    })
    .filter(Boolean);
}

// Demo data for previewing the render without real metafield data. Mirrors the
// real saved_lists shape: each item is only { id (variantId), quantity, selected }.
// Product display data is resolved from variant ids via fetchListProducts.
// Enable by appending `?yl-demo=1` to the page URL. Never used otherwise.
const DEMO_LISTS = [
  {
    id: '222222',
    name: 'October Stock',
    updatedAt: '2025-02-20',
    items: [
      { id: '42484596572254', title: 'Organic Golden Sugar - 500g', quantity: 2, selected: true },
      { id: '42716782231646', title: 'Organic Golden Sugar - 500g', quantity: 0, selected: false },
      { id: '42716782821470', title: 'Organic Roasted Seaweed Multipack', quantity: 0, selected: false },
      { id: '42716901212254', title: 'Organic Puffed Stars Cheese', quantity: 0, selected: false },
      // Unresolved variant → falls into the "Others" group.
      { id: '00000000000000', title: 'Discontinued Mystery Product', quantity: 0, selected: false },
    ],
  },
  { id: '111111', name: 'Trending', updatedAt: '2025-03-28', items: [] },
  { id: '333333', name: 'November Refill', updatedAt: '2025-10-09', items: [] },
  { id: '444444', name: 'Easter Shopping List', updatedAt: '2024-03-11', items: [] },
];

function initYourList() {
  const mountPoint = document.getElementById('your-lists');

  if (!mountPoint) return;

  // Every load starts from the saved lists, refresh included — unsaved edits
  // only last while the page stays loaded (see listEdits.js, COB2B-785).
  clearAllEdits();

  const pageTitle = mountPoint.dataset.pageTitle || '';
  const accessToken = mountPoint.dataset.accessToken || '';
  const badgeIcon = mountPoint.dataset.badgeIcon || '';
  const noImage = mountPoint.dataset.noImage || '';
  let initialLists = normalizeLists(readJson(mountPoint, '[data-your-lists-data]', []));
  const i18n = decodeI18n(readJson(mountPoint, '[data-your-lists-i18n]', {}));
  // Tag badge config (from theme settings); keep only configured tags.
  let tagBadges = readJson(mountPoint, '[data-your-lists-tag-badges]', []).filter(
    (b) => b && b.tag,
  );

  const isDemo = window.location.search.includes('yl-demo');
  if ((!Array.isArray(initialLists) || initialLists.length === 0) && isDemo) {
    initialLists = DEMO_LISTS;
  }
  // Demo fallback so `?yl-demo` shows a tag badge even without store settings.
  if (isDemo && tagBadges.length === 0) {
    tagBadges = [{ tag: 'new', text: 'NEW', colour: '#000000' }];
  }

  render(
    <YourLists
      pageTitle={pageTitle}
      accessToken={accessToken}
      badgeIcon={badgeIcon}
      noImage={noImage}
      tagBadges={tagBadges}
      initialLists={initialLists}
      freshLists={freshLists}
      i18n={i18n}
    />,
    mountPoint,
  );
}

document.addEventListener('DOMContentLoaded', initYourList);

// Coming Back here from a PLP/PDP can restore this page from the bfcache with
// the lists as they were before that page added to them (COB2B-785). Reload so
// it reads them from the server again.
window.addEventListener('pageshow', (event) => {
  if (event.persisted && document.getElementById('your-lists')) {
    window.location.reload();
  }
});
